import React, { useMemo, useState } from 'react';
import { delta as calcDelta, pct, downloadCsv } from '../lib/format';

export const Spinner = ({ label = 'Loading report…' }) => (
  <div className="spinner-box"><span className="spinner" />{label}</div>
);

export const ErrorBox = ({ message }) => (
  <div className="error-box">⚠ {message}</div>
);

export const Empty = ({ children = 'No data for this period.' }) => (
  <div className="empty">{children}</div>
);

// Percentage change vs previous period. invert=true when a decrease is good
// (refunds, discounts, failed orders).
export const Delta = ({ curr, prev, invert = false }) => {
  const d = calcDelta(curr, prev);
  if (!d) return <span className="delta flat">—</span>;
  const good = d.dir === 'flat' ? 'flat' : (d.dir === 'up') !== invert ? 'up' : 'down';
  const arrow = d.dir === 'up' ? '▲' : d.dir === 'down' ? '▼' : '·';
  return <span className={`delta ${good}`}>{arrow} {pct(Math.abs(d.f))}</span>;
};

// `tour` tags a panel as a guided-tour / help anchor (see help/content.js).
export const KpiCard = ({ label, value, prev, curr, sub, invert, tour }) => (
  <div className="card kpi" data-tour={tour}>
    <div className="kpi-label">{label}</div>
    <div className="kpi-value">{value}</div>
    <div className="kpi-sub">
      {prev !== undefined && <Delta curr={curr} prev={prev} invert={invert} />}
      {sub && <span style={{ marginLeft: prev !== undefined ? 8 : 0 }}>{sub}</span>}
    </div>
  </div>
);

export const Card = ({ title, sub, actions, children, tour }) => (
  <div className="card" data-tour={tour}>
    {(title || actions) && (
      <div className="card-head">
        <div>
          {title && <h3>{title}</h3>}
          {sub && <div className="sub">{sub}</div>}
        </div>
        <div className="spacer" />
        {actions}
      </div>
    )}
    {children}
  </div>
);

// Sortable data table.
// columns: [{ key, label, num?, render?(row), sortValue?(row), csv?(row), footer? }]
export function DataTable({ columns, rows, defaultSort, csvName, footer, maxRows }) {
  const [sort, setSort] = useState(defaultSort || null); // { key, dir }
  const [expanded, setExpanded] = useState(false);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find(c => c.key === sort.key);
    const val = r => (col?.sortValue ? col.sortValue(r) : r[sort.key]);
    return [...rows].sort((a, b) => {
      const av = val(a), bv = val(b);
      const cmp = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av ?? '').localeCompare(String(bv ?? ''));
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [rows, sort, columns]);

  const visible = maxRows && !expanded ? sorted.slice(0, maxRows) : sorted;

  const clickSort = (col) => {
    if (col.noSort) return;
    setSort(s => s && s.key === col.key
      ? { key: col.key, dir: s.dir === 'desc' ? 'asc' : 'desc' }
      : { key: col.key, dir: col.num ? 'desc' : 'asc' });
  };

  return (
    <div>
      {csvName && (
        <div className="card-head" style={{ marginBottom: 4 }}>
          <div className="spacer" />
          <div className="table-actions">
            <button className="btn-ghost" onClick={() => downloadCsv(csvName, columns, sorted)}>
              ⬇ CSV
            </button>
          </div>
        </div>
      )}
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              {columns.map(col => (
                <th key={col.key}
                    className={`${col.num ? 'num' : ''} ${col.noSort ? 'no-sort' : ''}`}
                    onClick={() => clickSort(col)}>
                  {col.label}
                  {sort?.key === col.key && (
                    <span className="arrow">{sort.dir === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row, i) => (
              <tr key={row.__key ?? i}>
                {columns.map(col => (
                  <td key={col.key} className={col.num ? 'num' : ''}>
                    {col.render ? col.render(row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {footer && (
            <tfoot>
              <tr>
                {columns.map(col => (
                  <td key={col.key} className={col.num ? 'num' : ''}>
                    {col.footer ? col.footer(rows) : ''}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {maxRows && sorted.length > maxRows && (
        <div style={{ textAlign: 'center', marginTop: 8 }}>
          <button className="btn-ghost" onClick={() => setExpanded(e => !e)}>
            {expanded ? 'Show less' : `Show all ${sorted.length}`}
          </button>
        </div>
      )}
    </div>
  );
}
